import { useSelector } from 'react-redux';

import { TProductInstance } from '@lib/core/products/types';
import { actionResetUserQuiz } from '@lib/core/quizzes/slices/userQuiz';
import { useRetailer } from '@lib/core/retailers/hooks/useRetailer';
import { actionGetRetailerDetail } from '@lib/core/retailers/slices';
import { selectKioskProducts, selectKioskProductsIsLoading } from '@lib/core/service/selectors/kioskRootSelector';
import { setIsKioskSessionResetInProgress } from '@lib/core/service/slices';
import { prependBasename } from '@lib/core/service/utils';
import { actionResetUserCharactersSlice } from '@lib/core/users/slices/characters';
import { actionResetDietaryPreferenceListData } from '@lib/core/users/slices/dietaryPreference';
import { detachFidelityCard } from '@lib/core/users/slices/fidelityCard';
import { actionResetProductList } from '@lib/core/users/slices/productList';
import { actionResetUserProfile } from '@lib/core/users/slices/profile';
import { resetCurrentProductTip } from '@lib/tools/productTip/slices/';
import { resetPromotionProducts } from '@lib/tools/promotionProducts/slices';
import { useTypedDispatch } from '@lib/tools/views/hooks/useTypedDispatch';
import { PAGES } from '@lib/tools/views/urls';

export const useKioskRoot = (): {
  handleResetKioskUserSession: (params: { showFidelityMessage?: boolean }) => void;
  products: TProductInstance[][];
  isProductsLoading: boolean;
} => {
  const dispatch = useTypedDispatch();

  const { retailerSlug } = useRetailer();

  const handleResetKioskUserSession = async ({ showFidelityMessage = false }: { showFidelityMessage?: boolean }) => {
    dispatch(setIsKioskSessionResetInProgress(true));

    try {
      await Promise.allSettled([
        dispatch(resetCurrentProductTip()),
        dispatch(detachFidelityCard({ displayMessage: showFidelityMessage })),
        dispatch(actionResetUserQuiz()),
        dispatch(resetPromotionProducts()),
        dispatch(actionResetProductList()),
        dispatch(actionResetDietaryPreferenceListData()),
        dispatch(actionResetUserProfile()),
        dispatch(actionGetRetailerDetail({ retailerSlug })),
        dispatch(actionResetUserCharactersSlice()),
      ]);
    } catch (error) {
      console.error('Error during reset actions:', error);
    } finally {
      window.location.href = prependBasename(PAGES.vinhood.welcome);
    }
  };

  return {
    handleResetKioskUserSession,
    isProductsLoading: useSelector(selectKioskProductsIsLoading),
    products: useSelector(selectKioskProducts),
  };
};
